import {inject, Injectable} from '@angular/core';
import {toSignal} from '@angular/core/rxjs-interop';
import {HttpClient} from '@angular/common/http';
import {DateTime} from 'luxon';
import {Observable, Subject, switchMap} from 'rxjs';
import {countdown, downloadResponseAsFile, mapParseItem} from '@brunata-angular/util';
import {environment} from 'src/environments/environment';
import {DeliveryLogPage, deliveryLogPageSchema} from './delivery-log.model';
import {
    SavedExport,
    SavedExportCreateRequest,
    savedExportFileName,
    SavedExportPage,
    savedExportPageSchema,
    savedExportSchema,
    SavedExportTestRequest,
} from './saved-export.model';

const TEST_COOLDOWN_SECONDS = 60;

const DELIVERY_LOG_PAGE_SIZE = 200;

@Injectable({
    providedIn: 'root',
})
export class SavedExportsService {
    private readonly baseUrl = environment.onlineServiceUrl;
    private readonly http = inject(HttpClient);

    private readonly testTriggered = new Subject<void>();

    readonly testCooldownSeconds = toSignal(
        this.testTriggered.pipe(switchMap(() => countdown(TEST_COOLDOWN_SECONDS))),
        {initialValue: 0},
    );

    startTestCooldown(): void {
        this.testTriggered.next();
    }

    getSavedExports(): Observable<SavedExportPage> {
        return this.http
            .get(`${this.baseUrl}/savedexports`, {withCredentials: true})
            .pipe(mapParseItem(savedExportPageSchema));
    }

    getSavedExport(savedExportId: string): Observable<SavedExport> {
        return this.http
            .get(`${this.baseUrl}/savedexports/${savedExportId}`, {withCredentials: true})
            .pipe(mapParseItem(savedExportSchema));
    }

    // The log holds one row per scheduled delivery, and the modal filters and sorts what it is
    // given, so a page covers the deliveries a customer can reasonably look through at once.
    getDeliveries(savedExportId: string | null): Observable<DeliveryLogPage> {
        const path = savedExportId === null ? '' : `/${savedExportId}`;
        return this.http
            .get(`${this.baseUrl}/savedexports${path}/deliveries`, {
                params: {pageSize: DELIVERY_LOG_PAGE_SIZE},
                withCredentials: true,
            })
            .pipe(mapParseItem(deliveryLogPageSchema));
    }

    createSavedExport(payload: SavedExportCreateRequest): Observable<SavedExport> {
        return this.http
            .post(`${this.baseUrl}/savedexports`, payload, {withCredentials: true})
            .pipe(mapParseItem(savedExportSchema));
    }

    updateSavedExport(
        savedExportId: string,
        payload: SavedExportCreateRequest,
    ): Observable<SavedExport> {
        return this.http
            .patch(`${this.baseUrl}/savedexports/${savedExportId}`, payload, {
                withCredentials: true,
            })
            .pipe(mapParseItem(savedExportSchema));
    }

    deleteSavedExport(savedExportId: string): Observable<void> {
        return this.http.delete<void>(`${this.baseUrl}/savedexports/${savedExportId}`, {
            withCredentials: true,
        });
    }

    // One-off dry run of a not-yet-saved schedule: the backend renders a sample file and emails it
    // to the recipients without persisting anything (backend AGI-19153, PR #981).
    sendTestDelivery(payload: SavedExportTestRequest): Observable<void> {
        return this.http.post<void>(`${this.baseUrl}/savedexports/test`, payload, {
            withCredentials: true,
        });
    }

    exportSavedExportNow(savedExportId: string): Observable<void> {
        return this.http.post<void>(`${this.baseUrl}/savedexports/${savedExportId}/export`, null, {
            withCredentials: true,
        });
    }

    downloadSavedExport(
        savedExport: SavedExport,
        readingDate: DateTime,
        timeZone: string,
    ): Observable<unknown> {
        return this.http
            .get(`${this.baseUrl}/savedexports/${savedExport.savedExportId}/download`, {
                params: {date: readingDate.toISODate() ?? '', timeZone},
                observe: 'response',
                responseType: 'blob',
                withCredentials: true,
            })
            .pipe(
                downloadResponseAsFile(() =>
                    savedExportFileName(savedExport.name, readingDate, savedExport.outputFormat),
                ),
            );
    }
}
